import { vendorInvoiceAccountingCalculationInputSchema, vendorInvoiceAccountingCalculationSchema, } from '../schemas/vendor-invoice-accounting.js';
import { moneySchema } from '../schemas/common.js';
import { addDecimal, compareDecimal, divideDecimal, multiplyDecimal, roundDecimal, subtractDecimal, } from './decimal.js';
const MONEY_SCALE = 4;
const RATE_SCALE = 6;
const MONEY_PRODUCT_SCALE = MONEY_SCALE * 2;
const DEDUCTION_PRODUCT_SCALE = MONEY_SCALE + RATE_SCALE;
const ZERO = '0.0000';
const sum = (values) => values.reduce((total, value) => addDecimal(total, value, MONEY_SCALE), ZERO);
const rateKey = (value) => roundDecimal(value, RATE_SCALE);
const proportional = (amount, share, total) => divideDecimal(multiplyDecimal(amount, share, MONEY_PRODUCT_SCALE), total, MONEY_SCALE);
/** Retain the usual last-share residual, but never exceed a source share or reverse its sign. */
export function allocateBoundedMoneyResidual(total, shares) {
    const result = shares.map(({ value, min, max }) => min !== null && compareDecimal(value, min) < 0
        ? min
        : max !== null && compareDecimal(value, max) > 0
            ? max
            : value);
    let remaining = subtractDecimal(total, sum(result), MONEY_SCALE);
    for (const [index, share] of [...shares.entries()].reverse()) {
        if (compareDecimal(remaining, ZERO) === 0)
            break;
        const value = result[index];
        if (value === undefined)
            return null;
        let adjustment = remaining;
        if (compareDecimal(remaining, ZERO) > 0 && share.max !== null) {
            const capacity = subtractDecimal(share.max, value, MONEY_SCALE);
            if (compareDecimal(adjustment, capacity) > 0)
                adjustment = capacity;
        }
        else if (compareDecimal(remaining, ZERO) < 0 && share.min !== null) {
            const capacity = subtractDecimal(share.min, value, MONEY_SCALE);
            if (compareDecimal(adjustment, capacity) < 0)
                adjustment = capacity;
        }
        result[index] = addDecimal(value, adjustment, MONEY_SCALE);
        remaining = subtractDecimal(remaining, adjustment, MONEY_SCALE);
    }
    return compareDecimal(remaining, ZERO) === 0 ? result : null;
}
function shareBounds(source, capped = false) {
    const sign = compareDecimal(source, ZERO);
    return {
        min: sign < 0 ? (capped ? source : null) : ZERO,
        max: sign > 0 ? (capped ? source : null) : ZERO,
    };
}
function unavailable(finding) {
    return {
        eligible: false,
        findings: [finding],
        netAmount: null,
        taxAmount: null,
        grossAmount: null,
        deductibleTaxAmount: null,
        homeGrossAmount: null,
        homeCostAmount: null,
        homeDeductibleTaxAmount: null,
        rows: [],
    };
}
/** Original tax totals remain authoritative; splitting a cost cannot create additional input VAT. */
export function calculateVendorInvoiceAccountingAllocation(input) {
    const parsed = vendorInvoiceAccountingCalculationInputSchema.safeParse(input);
    if (!parsed.success)
        return unavailable('invalidInput');
    const source = parsed.data;
    if (new Set(source.splits.map((row) => row.position)).size !== source.splits.length)
        return unavailable('duplicatePosition');
    const buckets = new Map();
    for (const bucket of source.buckets) {
        const key = rateKey(bucket.taxRate), previous = buckets.get(key) ?? { net: ZERO, tax: ZERO };
        buckets.set(key, {
            net: addDecimal(previous.net, bucket.netAmount, MONEY_SCALE),
            tax: addDecimal(previous.tax, bucket.taxAmount, MONEY_SCALE),
        });
    }
    const rows = [...source.splits]
        .sort((left, right) => left.position - right.position)
        .map((row) => ({
        ...row,
        netAmount: roundDecimal(row.netAmount, MONEY_SCALE),
        sourceTaxAmount: ZERO,
        deductibleTaxAmount: ZERO,
        costAmount: ZERO,
        homeCostAmount: ZERO,
        homeDeductibleTaxAmount: ZERO,
    }));
    if (rows.some((row) => !buckets.has(rateKey(row.taxRate))))
        return unavailable('allocationTaxRateMismatch');
    for (const [rate, bucket] of buckets) {
        const members = rows.filter((row) => rateKey(row.taxRate) === rate);
        if (compareDecimal(bucket.net, ZERO) !== 0 &&
            members.some((row) => compareDecimal(row.netAmount, ZERO) !== 0 &&
                compareDecimal(row.netAmount, ZERO) > 0 !== compareDecimal(bucket.net, ZERO) > 0))
            return unavailable('allocationSignMismatch');
        if (compareDecimal(sum(members.map((row) => row.netAmount)), bucket.net) !== 0)
            return unavailable('allocationTotalMismatch');
        if (compareDecimal(bucket.net, ZERO) === 0) {
            if (compareDecimal(bucket.tax, ZERO) !== 0 ||
                members.some((row) => compareDecimal(row.netAmount, ZERO) !== 0))
                return unavailable('zeroTaxBase');
        }
        else {
            if (compareDecimal(bucket.tax, ZERO) !== 0 &&
                compareDecimal(bucket.tax, ZERO) > 0 !== compareDecimal(bucket.net, ZERO) > 0)
                return unavailable('sourceTaxMismatch');
            let used = ZERO;
            const amounts = members.filter((row) => compareDecimal(row.netAmount, ZERO) !== 0);
            for (const [index, row] of amounts.entries()) {
                row.sourceTaxAmount =
                    index === amounts.length - 1
                        ? subtractDecimal(bucket.tax, used, MONEY_SCALE)
                        : proportional(bucket.tax, row.netAmount, bucket.net);
                used = addDecimal(used, row.sourceTaxAmount, MONEY_SCALE);
            }
        }
        const sourceAmounts = allocateBoundedMoneyResidual(bucket.tax, members.map((row) => ({ value: row.sourceTaxAmount, ...shareBounds(row.netAmount) })));
        if (!sourceAmounts)
            return unavailable('sourceTaxMismatch');
        for (const [index, row] of members.entries()) {
            const amount = sourceAmounts[index];
            if (amount === undefined)
                return unavailable('sourceTaxMismatch');
            row.sourceTaxAmount = amount;
        }
        const deductions = new Map();
        for (const row of members) {
            const key = rateKey(row.inputTaxDeductionPercent), previous = deductions.get(key) ?? [];
            previous.push(row);
            deductions.set(key, previous);
        }
        for (const [percentage, group] of deductions) {
            const totalTax = sum(group.map((row) => row.sourceTaxAmount));
            const totalDeductible = divideDecimal(multiplyDecimal(totalTax, percentage, DEDUCTION_PRODUCT_SCALE), '100', MONEY_SCALE);
            let used = ZERO;
            const amounts = group.filter((row) => compareDecimal(row.sourceTaxAmount, ZERO) !== 0);
            for (const [index, row] of amounts.entries()) {
                row.deductibleTaxAmount =
                    index === amounts.length - 1
                        ? subtractDecimal(totalDeductible, used, MONEY_SCALE)
                        : divideDecimal(multiplyDecimal(row.sourceTaxAmount, percentage, DEDUCTION_PRODUCT_SCALE), '100', MONEY_SCALE);
                used = addDecimal(used, row.deductibleTaxAmount, MONEY_SCALE);
            }
            const deductibleAmounts = allocateBoundedMoneyResidual(totalDeductible, amounts.map((row) => ({
                value: row.deductibleTaxAmount,
                ...shareBounds(row.sourceTaxAmount, true),
            })));
            if (!deductibleAmounts)
                return unavailable('sourceTaxMismatch');
            for (const [index, row] of amounts.entries()) {
                const amount = deductibleAmounts[index];
                if (amount === undefined)
                    return unavailable('sourceTaxMismatch');
                row.deductibleTaxAmount = amount;
            }
        }
    }
    const netAmount = sum(source.buckets.map((bucket) => bucket.netAmount)), taxAmount = sum(source.buckets.map((bucket) => bucket.taxAmount));
    const grossAmount = addDecimal(netAmount, taxAmount, MONEY_SCALE);
    const deductibleTaxAmount = sum(rows.map((row) => row.deductibleTaxAmount));
    const homeGrossAmount = multiplyDecimal(grossAmount, source.exchangeRate, MONEY_SCALE);
    const homeDeductibleTaxAmount = multiplyDecimal(deductibleTaxAmount, source.exchangeRate, MONEY_SCALE);
    const homeCostAmount = subtractDecimal(homeGrossAmount, homeDeductibleTaxAmount, MONEY_SCALE);
    for (const row of rows) {
        row.costAmount = addDecimal(row.netAmount, subtractDecimal(row.sourceTaxAmount, row.deductibleTaxAmount, MONEY_SCALE), MONEY_SCALE);
    }
    const costs = rows.filter((row) => compareDecimal(row.costAmount, ZERO) !== 0);
    let usedCost = ZERO;
    for (const [index, row] of costs.entries()) {
        row.homeCostAmount =
            index === costs.length - 1
                ? subtractDecimal(homeCostAmount, usedCost, MONEY_SCALE)
                : multiplyDecimal(row.costAmount, source.exchangeRate, MONEY_SCALE);
        usedCost = addDecimal(usedCost, row.homeCostAmount, MONEY_SCALE);
    }
    const taxes = rows.filter((row) => compareDecimal(row.deductibleTaxAmount, ZERO) !== 0);
    let usedTax = ZERO;
    for (const [index, row] of taxes.entries()) {
        row.homeDeductibleTaxAmount =
            index === taxes.length - 1
                ? subtractDecimal(homeDeductibleTaxAmount, usedTax, MONEY_SCALE)
                : multiplyDecimal(row.deductibleTaxAmount, source.exchangeRate, MONEY_SCALE);
        usedTax = addDecimal(usedTax, row.homeDeductibleTaxAmount, MONEY_SCALE);
    }
    const boundedCosts = allocateBoundedMoneyResidual(homeCostAmount, costs.map((row) => ({ value: row.homeCostAmount, ...shareBounds(row.costAmount) })));
    const boundedTaxes = allocateBoundedMoneyResidual(homeDeductibleTaxAmount, taxes.map((row) => ({
        value: row.homeDeductibleTaxAmount,
        ...shareBounds(row.deductibleTaxAmount),
    })));
    if (!boundedCosts || !boundedTaxes)
        return unavailable('amountOverflow');
    for (const [index, row] of costs.entries()) {
        const amount = boundedCosts[index];
        if (amount === undefined)
            return unavailable('amountOverflow');
        row.homeCostAmount = amount;
    }
    for (const [index, row] of taxes.entries()) {
        const amount = boundedTaxes[index];
        if (amount === undefined)
            return unavailable('amountOverflow');
        row.homeDeductibleTaxAmount = amount;
    }
    if (![
        netAmount,
        taxAmount,
        grossAmount,
        deductibleTaxAmount,
        homeGrossAmount,
        homeCostAmount,
        homeDeductibleTaxAmount,
    ].every((value) => moneySchema.safeParse(value).success))
        return unavailable('amountOverflow');
    const result = vendorInvoiceAccountingCalculationSchema.safeParse({
        eligible: true,
        findings: [],
        netAmount,
        taxAmount,
        grossAmount,
        deductibleTaxAmount,
        homeGrossAmount,
        homeCostAmount,
        homeDeductibleTaxAmount,
        rows,
    });
    return result.success ? result.data : unavailable('amountOverflow');
}
//# sourceMappingURL=vendor-invoice-accounting.js.map